import type {
  AdminUserPlan,
  MealAdherenceStateName,
  PlanDayView,
  PlanSafetyHoldView,
  PlannedItemView,
  PlannedMealView,
} from '@steado/admin-contract/user-detail';
import { useQuery } from '@tanstack/react-query';

import { BadgeCell } from '@/components/table/cells';
import type { ToneName } from '@/components/table/tone';
import { useAuth } from '@/lib/auth/auth-context';
import { planTone } from '@/lib/plan-tone';

import { Empty, Panel, TabSkeleton } from '@/components/panel/panels';
import { MealPlanPanel } from './MealPlanPanel';
import { PlanRenewalPanel } from './PlanRenewalPanel';
import { PlanSafetyHoldPanel } from './PlanSafetyHoldPanel';
import { TabError } from './TabError';
import { formatDate } from '@/lib/format';
import { userPlanQueryOptions } from './user-detail-query';

interface PlanTabProps {
  readonly userId: string;
  /**
   * From the detail query, not the plan one.
   *
   * The hold lives on the user rather than on a week — it survives one week
   * ending and the next refusing to generate — but it is read beside the week
   * it is holding, so it is rendered here and fetched there.
   */
  readonly hold: PlanSafetyHoldView;
}

export function PlanTab({ userId, hold }: PlanTabProps) {
  const { api } = useAuth();
  const query = useQuery(userPlanQueryOptions(api, userId, true));

  if (query.isPending) return <TabSkeleton rows={5} />;
  if (query.isError) {
    return <TabError message={query.error.message} onRetry={() => void query.refetch()} />;
  }

  return <PlanContent plan={query.data} userId={userId} hold={hold} />;
}

function PlanContent({
  plan,
  userId,
  hold,
}: {
  readonly plan: AdminUserPlan;
  readonly userId: string;
  readonly hold: PlanSafetyHoldView;
}) {
  /**
   * Beside the weekly week, and present whether or not there is one: a meal
   * plan is its own row with its own lifetime, and an account can hold one
   * while no week has ever generated.
   */
  const planFacts = (
    <div className="grid grid-cols-1 items-start gap-3 xl:grid-cols-2">
      <MealPlanPanel mealPlan={plan.mealPlan} />
      <PlanRenewalPanel
        renewal={plan.renewal}
        entitlement={plan.entitlement}
        mealPlanMode={plan.mealPlanMode}
      />
    </div>
  );

  if (plan.current === null) {
    return (
      <div className="flex flex-col gap-3">
        <Panel title="Plan">
          <Empty
            title="No plan has been generated"
            body="Weeks appear here once generation has run for this user."
          />
        </Panel>

        {planFacts}

        {/* Still shown: a hold is the most likely reason there is no week. */}
        <PlanSafetyHoldPanel userId={userId} hold={hold} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {plan.program === null ? null : <ProgramPanel program={plan.program} />}

      <Panel
        title={`Week of ${formatDate(`${plan.current.weekStartDate}T00:00:00.000Z`)}`}
        action={<BadgeCell label={plan.current.status} tone={planTone(plan.current.status)} dot />}
        bleed
      >
        {plan.current.rationale === null ? null : (
          <p className="border-t border-border px-[18px] py-3 text-[13px] leading-relaxed text-pretty text-muted-foreground">
            {plan.current.rationale}
          </p>
        )}
        {plan.current.failureReason === null ? null : (
          <p
            role="alert"
            className="border-t border-border px-[18px] py-3 text-[13px] leading-relaxed text-err-fg"
          >
            {plan.current.failureReason}
          </p>
        )}

        <ul>
          {plan.current.days.map((day) => (
            <PlanDayRow key={day.id} day={day} />
          ))}
        </ul>
      </Panel>

      {planFacts}

      <div className="grid grid-cols-1 items-start gap-3 xl:grid-cols-[1.3fr_1fr]">
        <Panel title="Week history" bleed>
          {plan.history.length === 0 ? (
            <p className="border-t border-border px-[18px] py-3.5 text-[13px] text-muted-foreground">
              This is the only week generated so far.
            </p>
          ) : (
            <ul>
              {plan.history.map((week) => (
                <li
                  key={week.id}
                  className="flex items-center gap-2.5 border-t border-border px-[18px] py-2.5"
                >
                  <span className="shrink-0 font-mono text-[13px] text-foreground">{week.weekStartDate}</span>
                  <span className="min-w-0 flex-1 truncate text-[13px] text-muted-foreground">
                    {week.failureReason ?? week.rationale ?? ''}
                  </span>
                  <BadgeCell label={week.status} tone={planTone(week.status)} />
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <PlanSafetyHoldPanel userId={userId} hold={hold} />
      </div>
    </div>
  );
}

/**
 * What the program asked for in the week the plan below covers.
 *
 * Read-only, and read-only structurally rather than by omission: the row is
 * frozen by a database trigger the moment the program is laid out, so an
 * editor here would be a control that cannot work. It sits above the week
 * because that is the comparison it exists for — the program prescribed this,
 * and the plan underneath may have drifted from it.
 */
function ProgramPanel({ program }: { readonly program: NonNullable<AdminUserPlan['program']> }) {
  const week = program.currentWeek;

  return (
    <Panel
      title="Program"
      action={
        week === null ? null : (
          <span className="font-mono text-xs text-muted-foreground">
            week {String(week.index)} of {String(program.weekCount)}
          </span>
        )
      }
      bleed
    >
      <div className="flex flex-col gap-2 border-t border-border px-[18px] pt-3 pb-3.5">
        <div className="flex flex-wrap items-baseline gap-2.5">
          <span className="text-[13px] font-medium text-foreground">{program.name}</span>
          <span className="font-mono text-xs text-muted-foreground">from {program.startDate}</span>
          {week === null ? null : <BadgeCell label={week.phase} tone="mute" />}
        </div>

        {week === null ? (
          <p className="text-[13px] text-muted-foreground">Not linked to a program week.</p>
        ) : (
          <p className="text-[13px] leading-relaxed text-muted-foreground">
            {String(week.sessionsPerWeek)} sessions · {String(week.setsPerMovement)} sets ·{' '}
            {String(week.repRange.low)}–{String(week.repRange.high)} reps · {String(week.targetRir)} RIR ·{' '}
            {week.movementPatterns.map((pattern) => pattern.toLowerCase().replace(/_/g, ' ')).join(', ')}
          </p>
        )}
      </div>
    </Panel>
  );
}

function PlanDayRow({ day }: { readonly day: PlanDayView }) {
  const isRest = day.workout === null || day.workout.isRestDay;

  return (
    <li className="flex flex-col gap-1.5 border-t border-border px-[18px] pt-3 pb-3.5">
      <div className="flex items-baseline gap-2.5">
        <span className="w-9 shrink-0 text-[11.5px] font-semibold tracking-[0.055em] text-muted-foreground uppercase">
          {day.dayOfWeek}
        </span>
        <span className="text-[13px] font-medium text-foreground">
          {/* A rest day is a row the generator wrote, not an absent one. */}
          {isRest ? 'Rest day' : day.workout?.title}
        </span>
      </div>

      {isRest || day.workout === null || day.workout.items.length === 0 ? null : (
        <ul className="ml-[46px] flex flex-col gap-0.5">
          {day.workout.items.map((item) => (
            <li key={item.id} className="flex items-baseline gap-2 text-xs">
              <span className="w-[74px] shrink-0 font-mono text-muted-foreground">{item.kind}</span>
              <span className="min-w-0 flex-1 truncate text-muted-foreground">
                {item.name}
                {item.note === null ? null : <span className="text-muted-foreground"> · {item.note}</span>}
              </span>
              <span className="shrink-0 font-mono text-muted-foreground">{prescriptionOf(item)}</span>
            </li>
          ))}
        </ul>
      )}

      {day.meals.length === 0 ? null : (
        <ul className="ml-[46px] flex flex-col gap-0.5">
          {day.meals.map((meal) => (
            <li key={meal.id} className="flex items-baseline gap-2 text-xs">
              <span className="w-10 shrink-0 font-mono text-muted-foreground">{meal.timeLocal}</span>
              {/*
                Catalog rows now, not a string array the model wrote. Each item
                names a real food and the portion the server solved for, which
                is why the quantity is worth showing at all — it is derived,
                not asserted.
              */}
              <span className="min-w-0 flex-1 truncate text-muted-foreground">
                {meal.items.length === 0
                  ? '—'
                  : meal.items
                      .map((item) =>
                        item.portionLabel === null
                          ? item.name
                          : `${item.name} (${String(item.portions)}× ${item.portionLabel})`,
                      )
                      .join(', ')}
              </span>
              <span className="shrink-0 font-mono text-muted-foreground">{String(meal.kcal)} kcal</span>
              <AdherenceMark adherence={meal.adherence} />
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

/**
 * The prescription in the day's right-hand column, shaped by the item's kind:
 * resistance carries sets/reps/rest, timed work carries a duration, and a held
 * movement carries rounds of it (the admin contract sends the raw columns). The
 * per-kind fields are nullable in the contract, so each segment appears only
 * when its fields are present.
 */
function prescriptionOf(item: PlannedItemView): string {
  if (item.kind === 'resistance') {
    const segments: string[] = [];
    if (item.sets !== null && item.repsLow !== null && item.repsHigh !== null) {
      segments.push(`${String(item.sets)}×${String(item.repsLow)}–${String(item.repsHigh)}`);
    }
    if (item.restSeconds !== null) segments.push(`${String(item.restSeconds)}s rest`);
    if (item.targetRir !== null) segments.push(`RIR ${String(item.targetRir)}`);
    return segments.length === 0 ? '—' : segments.join(' · ');
  }

  if (item.durationSeconds === null) return '—';
  // A held movement (a plank) is rounds of one hold: "3×45s · 30s rest".
  if (item.sets !== null && item.sets > 1) {
    const rounds = `${String(item.sets)}×${formatSpan(item.durationSeconds)}`;
    return item.restSeconds === null ? rounds : `${rounds} · ${String(item.restSeconds)}s rest`;
  }
  return formatSpan(item.durationSeconds);
}

/** `1200` → "20 min", `750` → "12m 30s", `45` → "45s". */
function formatSpan(seconds: number): string {
  if (seconds < 60) return `${String(seconds)}s`;

  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return rest === 0 ? `${String(minutes)} min` : `${String(minutes)}m ${String(rest)}s`;
}

/**
 * What the user said about the meal, or nothing at all.
 *
 * Deliberately blank when unanswered rather than a "pending" badge: most meals
 * in most weeks have no answer, and a column of grey chips would read as a
 * problem with the plan instead of as the ordinary state.
 */
function AdherenceMark({ adherence }: { readonly adherence: PlannedMealView['adherence'] }) {
  if (adherence === null) return <span className="w-16 shrink-0" />;

  const label =
    adherence.fraction === null ? adherence.state : `${String(Math.round(adherence.fraction * 100))}% eaten`;

  return (
    <span className="w-16 shrink-0 text-right">
      <BadgeCell label={label} tone={ADHERENCE_TONES[adherence.state]} />
    </span>
  );
}

/**
 * No fallback branch, for the same reason `plan-tone.ts` has none: the state is
 * a closed `z.enum`, so a member this build has never heard of fails the
 * response parse rather than arriving here to be coloured grey — and the
 * exhaustive `Record` makes a new one a compile error.
 */
const ADHERENCE_TONES: Readonly<Record<MealAdherenceStateName, ToneName>> = {
  eaten: 'ok',
  partial: 'info',
  // Not an error — a skipped meal is a normal thing a person does — but it is
  // the one an operator is looking for.
  skipped: 'warn',
  // Ate something, just not the plan — distinct from both "as planned" and
  // "skipped entirely", so it gets its own tone rather than borrowing one.
  swapped: 'purple',
};
